import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IstDatePipe } from '../../../pipes/ist-date.pipe';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { Router } from '@angular/router';
import {
  AdminOrder,
  AdminOrderLookupResult,
  AdminOrderLookupType,
  OrdersService,
} from '../../../services/orders/orders.service';

@Component({
  selector: 'app-order-lookup',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, IstDatePipe],
  templateUrl: './order-lookup.component.html',
})
export class OrderLookupComponent {
  private ordersService = inject(OrdersService);
  private snackbar = inject(SnackbarService);
  private router = inject(Router);

  searchQuery = '';
  isLoading = signal(false);
  lookupResult = signal<AdminOrderLookupResult | null>(null);
  selectedOrder = signal<AdminOrder | null>(null);

  lookup() {
    const query = this.searchQuery.trim();
    if (!query) {
      this.snackbar.warning('Enter an order ID, order number, or rider ID');
      return;
    }

    this.isLoading.set(true);
    this.lookupResult.set(null);
    this.selectedOrder.set(null);

    this.ordersService.lookupAdminOrders(query).subscribe({
      next: (result) => {
        this.lookupResult.set(result);
        if (result.orders.length === 1) {
          this.selectOrder(result.orders[0]);
        } else if (!result.orders.length) {
          this.snackbar.info(result.message || 'No orders found');
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Order lookup failed:', err);
        this.snackbar.error('Could not look up order');
        this.isLoading.set(false);
      },
    });
  }

  selectOrder(order: AdminOrder) {
    this.isLoading.set(true);
    this.ordersService.getStoreAdminOrderDetails(order.order_id).subscribe({
      next: (fullOrder) => {
        this.selectedOrder.set(fullOrder);
        this.isLoading.set(false);
      },
      error: () => {
        this.selectedOrder.set(order);
        this.isLoading.set(false);
      },
    });
  }

  lookupTypeLabel(type: AdminOrderLookupType | undefined): string {
    switch (type) {
      case 'order_id':
        return 'Matched by order ID';
      case 'order_number':
        return 'Matched by order number';
      case 'rider_id':
        return 'Matched by rider ID (orders.rider_id)';
      case 'rider_user_id':
        return 'Matched by rider user account ID';
      case 'customer_user_id':
        return 'Matched by customer user ID';
      case 'not_found':
        return 'No results';
      default:
        return '';
    }
  }

  getPromotionTotal(order: AdminOrder): number {
    return (order.promotions || []).reduce(
      (sum, promotion) => sum + Number(promotion.discount_amount || 0),
      0
    );
  }

  riderDisplayName(order: AdminOrder): string {
    return (
      order.rider?.full_name ||
      order.rider?.name ||
      order.rider?.code ||
      'Unassigned'
    );
  }
}
